import React, { useEffect, useState, useMemo } from 'react';
import { getCombinedOverviewAiData } from '../api';
import './Overview.css';
import OverviewPlaceholder from '../components/OverviewPlaceholder';
import OverviewUpgradePlaceholder from '../components/OverviewUpgradePlaceholder';
import ExecutiveSummarySection from '../components/overview/ExecutiveSummarySection';
import PainPointsSection from '../components/overview/PainPointsSection';
import ImmediatePrioritiesSection from '../components/overview/ImmediatePrioritiesSection';
import SimpleSlider from '../components/overview/SimpleSlider';
import SuccessesSection from '../components/overview/SuccessesSection';
import TopContributorsSection from '../components/overview/TopContributorsSection';
import PerformanceGapsSection from '../components/overview/PerformanceGapsSection';
import OtherInsightsSection from '../components/overview/OtherInsightsSection';
import CustomersNeedingAttentionSection from '../components/overview/CustomersNeedingAttentionSection';
import NotableTrendsSection from '../components/overview/NotableTrendsSection';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import LoadingSpinner from '../components/LoadingSpinner';

// CSS styles for responsive behavior
const overviewStyles = `
  .overview-container {
    margin: 0 auto;
    padding: 2rem;
    max-width: 100%;
    overflow-x: hidden;
  }
  
  .overview-flex-container {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }
  
  .overview-column {
    flex: 1 1 400px;
    min-width: 300px;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .overview-chart-padding {
    padding: 1.5rem;
  }
  
  @media (max-width: 768px) {
    .overview-container {
      padding: 1rem;
    }
    
    .overview-flex-container {
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .overview-column {
      flex: 1 1 100%;
      min-width: 100%;
      max-width: 100%;
      gap: 1rem;
    }
    
    .overview-chart-padding {
      padding: 1rem;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('overview-responsive-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'overview-responsive-styles';
    style.textContent = overviewStyles;
    document.head.appendChild(style);
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Overview: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showVelocityTooltip, setShowVelocityTooltip] = useState(false);
  const [showCsatTooltip, setShowCsatTooltip] = useState(false);
  const [isGenerationPossible, setIsGenerationPossible] = useState(false);
  const [needsPlanUpgrade, setNeedsPlanUpgrade] = useState(false);

  // Calculate the earliest sync date from active integrations
  const lastSyncDate = useMemo(() => {
    if (!data?.dashboardMetrics?.activeIntegrations || data.dashboardMetrics.activeIntegrations.length === 0) {
      return undefined;
    }

    const validDates = data.dashboardMetrics.activeIntegrations
      .map((integration: any) => integration.lastRunTime)
      .filter((date: string | null) => date !== null)
      .map((date: string) => new Date(date));

    if (validDates.length === 0) {
      return undefined;
    }

    // Find the latest date
    const latestDate = new Date(Math.max(...validDates.map((date: Date) => date.getTime())));
    return latestDate.toISOString();
  }, [data?.dashboardMetrics?.activeIntegrations]);

  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (showVelocityTooltip) {
        // Check if click is outside velocity tooltip and info icon
        if (!target.closest('[data-velocity-tooltip]') && !target.closest('[data-velocity-info-icon]')) {
          setShowVelocityTooltip(false);
        }
      }

      if (showCsatTooltip) {
        // Check if click is outside CSAT tooltip and info icon
        if (!target.closest('[data-csat-tooltip]') && !target.closest('[data-csat-info-icon]')) {
          setShowCsatTooltip(false);
        }
      }
    };

    if (showVelocityTooltip || showCsatTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVelocityTooltip, showCsatTooltip]);

  const velocityChartData = React.useMemo(() => {
    if (!data?.dashboardMetrics?.velocityMonthlyData || Object.keys(data.dashboardMetrics.velocityMonthlyData).length === 0) {
      return { labels: [], datasets: [] };
    }

    const projectNames = Object.keys(data.dashboardMetrics.velocityMonthlyData);
    const colors = ['#3182ce', '#e53e3e', '#38a169', '#d69e2e', '#805ad5', '#dd6b20'];

    // Get all unique months across all projects for labels
    const allMonths = new Set<string>();
    projectNames.forEach(projectName => {
      data.dashboardMetrics.velocityMonthlyData[projectName].forEach((item: any) => {
        allMonths.add(item.month);
      });
    });

    const sortedMonths = Array.from(allMonths).sort();

    // Convert YYYY-MM format to readable month labels
    const monthLabels = sortedMonths.map(monthKey => {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });

    const datasets = projectNames.map((projectName, index) => {
      const projectData = data.dashboardMetrics.velocityMonthlyData[projectName];
      const dataPoints = sortedMonths.map(monthKey => {
        const monthData = projectData.find((item: any) => item.month === monthKey);
        return monthData ? monthData.velocityPercentage : null;
      });

      return {
        label: `${projectName} Velocity %`,
        data: dataPoints,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length] + '20',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        spanGaps: true, // Connect across missing months
      };
    });

    return {
      labels: monthLabels,
      datasets: datasets,
    };
  }, [data?.dashboardMetrics?.velocityMonthlyData]);

  // Prepare CSAT chart data
  const csatChartData = React.useMemo(() => {
    if (!data?.dashboardMetrics?.csatMonthlyData || data.dashboardMetrics.csatMonthlyData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Convert YYYY-MM format to readable month labels
    const monthLabels = data.dashboardMetrics.csatMonthlyData.map((item: any) => {
      const [year, month] = item.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });

    const scores = data.dashboardMetrics.csatMonthlyData.map((item: any) => item.score);

    return {
      labels: monthLabels,
      datasets: [
        {
          label: 'CSAT Score',
          data: scores,
          borderColor: '#38a169',
          backgroundColor: 'rgba(56, 161, 105, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [data?.dashboardMetrics?.csatMonthlyData]);

  const csatChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 1,
        ticks: {
          stepSize: 0.2,
          callback: function (value: any) {
            return (value * 100) + '%';
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
    },
  };

  // Load existing summary on component mount
  useEffect(() => {
    loadSummary(false);
  }, []);

  const loadSummary = async (generateIfMissing: boolean, refresh?: boolean) => {
    try {
      setError('');
      setNeedsPlanUpgrade(false);
      if (generateIfMissing) {
        setIsGenerating(true);
      }

      const result = await getCombinedOverviewAiData(generateIfMissing, refresh);
      setIsGenerationPossible(true);
      setData(result);
    } catch (err: any) {
      // Check if this is a plan upgrade error
      if (err.type === 'PLAN_UPGRADE_REQUIRED') {
        setNeedsPlanUpgrade(true);
        setIsGenerationPossible(false);
        setData(null);
        setError('');
      } else if (err.message.includes('Not enough data to generate an AI overview')) {
        // This is expected when no sync has been run on an integration
        setIsGenerationPossible(false)
        setData(null);
      }
      else if (err.message.includes('No Summary generated.')) {
        // This is expected when no summary exists and generateIfMissing is false
        setIsGenerationPossible(true);
        setData(null);
      } else {
        setIsGenerationPossible(false);
        setError(err.message || 'Failed to load overview data');
      }
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleGenerateSummary = () => {
    loadSummary(true);
  };

  const handleReGenerateSummary = () => {
    loadSummary(true, true);
  };

  // Show error state
  if (error) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#e53e3e',
        background: '#fed7d7',
        borderRadius: '8px',
        margin: '2rem'
      }}>
        <h3>Error Loading Overview</h3>
        <p>{error}</p>
        <button
          onClick={() => loadSummary(false)}
          style={{
            background: '#3182ce',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner title="Loading overview data..." />;
  }

  // Show plan upgrade placeholder if needed
  if (needsPlanUpgrade) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        padding: '2rem'
      }}>
        <OverviewUpgradePlaceholder
          onUpgrade={() => {
            window.location.href = '/settings?tab=accounts-billing';
          }}
        />
      </div>
    );
  }

  // Show placeholder if no data exists
  if (!data || !data.aiOverview) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <OverviewPlaceholder
          onGenerateSummary={handleGenerateSummary}
          isGenerating={isGenerating}
          isGenerationPossible={isGenerationPossible}
        />
      </div>
    );
  }

  // Show the actual overview data
  const { aiOverview, summaryDate, dashboardMetrics } = data;

  const velocityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: velocityChartData.datasets.length > 1,
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (value: any) {
            return value + '%';
          },
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
    },
  };



  return (
    <div className="overview-container">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2.0rem',
          fontFamily: 'Raleway, sans-serif',
          fontWeight: 300,
          color: '#1a202c',
          margin: '0.0rem'
        }}>
          Executive Overview
        </h1>
      </div>

      {/* Executive Summary - Full Width Above Columns */}
      <div style={{ marginBottom: '2rem' }}>
        <ExecutiveSummarySection
          summary={aiOverview.executiveSummary}
          summaryDate={summaryDate}
          onRegenerate={handleReGenerateSummary}
          isGenerating={isGenerating}
          lastSyncDate={lastSyncDate}
        />
      </div>
      
      {/* Mobile: Immediate Priorities after Executive Summary */}      
      <div className="mobile-immediate-priorities">
        <SimpleSlider priorities={aiOverview.immediatePriorities || []} />
      </div>

      

      

      {/* 3-Column Flex Layout with Responsive Breakpoints */}
      <div className="overview-flex-container">
        {/* Column 1 */}
        <div className="overview-column">
          <CustomersNeedingAttentionSection customers={aiOverview.customersNeedingAttention || []} />
          {/* CSAT Chart - Full Width Below Columns */}
          {dashboardMetrics?.csatMonthlyData && dashboardMetrics.csatMonthlyData.length > 0 && (
            <div style={{ margin: '0rem' }}>
              <div className="overview-chart-padding" style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #38a169',
                height: '100%',
                minHeight: '300px',
                position: 'relative'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <h2 style={{
                    color: '#38a169',
                    margin: '0',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                  }}>
                    😊 Customer Satisfaction
                  </h2>
                  {/* Info Icon */}
                  <div
                    data-csat-info-icon
                    style={{
                      cursor: 'pointer',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCsatTooltip(!showCsatTooltip);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#007bff';
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                      const svg = e.currentTarget.querySelector('svg') as SVGElement;
                      if (svg) {
                        svg.style.stroke = 'white';
                        const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                        if (circle) circle.style.fill = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      const svg = e.currentTarget.querySelector('svg') as SVGElement;
                      if (svg) {
                        svg.style.stroke = '#666';
                        const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                        if (circle) circle.style.fill = '#666';
                      }
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#666"
                      strokeWidth="2"
                      style={{ transition: 'stroke 0.2s ease' }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="8" r="1" fill="#666" stroke="none" />
                      <line x1="12" y1="11" x2="12" y2="16" />
                    </svg>
                  </div>
                </div>
                {/* Tooltip */}
                {showCsatTooltip && (
                  <div
                    data-csat-tooltip
                    style={{
                      position: 'absolute',
                      top: '55px',
                      right: '10px',
                      backgroundColor: '#333',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      maxWidth: '280px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 1000,
                      border: '1px solid #555'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '15px' }}>
                      Customer Satisfaction
                    </div>
                    <div>
                      Shows the monthly average Customer Satisfaction (CSAT) scores as percentages (0-100%). Higher percentages indicate better customer satisfaction with support services. This helps track customer sentiment trends and identify opportunities for service improvement.
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '15px',
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#333',
                        transform: 'rotate(45deg)',
                        border: '1px solid #555',
                        borderBottom: 'none',
                        borderRight: 'none'
                      }}
                    />
                  </div>
                )}
                <div style={{ height: '200px', position: 'relative' }}>
                  <Line data={csatChartData} options={csatChartOptions} />
                </div>
                <p style={{
                  margin: '1rem 0 0',
                  fontSize: '0.875rem',
                  color: '#666',
                  textAlign: 'center'
                }}>
                  Monthly customer satisfaction scores (0-100%)
                </p>
              </div>
            </div>
          )}
          <SuccessesSection successes={aiOverview.successes || []} />

          

          {/*if there are no customers needing attention then put the other insights cpomponent here*/}
          {aiOverview.customersNeedingAttention?.length === 0 && (
            <OtherInsightsSection insights={aiOverview.otherInsights || []} />
          )}
        </div>

        {/* Column 2 */}
        <div className="overview-column">
          {/* Desktop: Show Immediate Priorities in column layout */}
          <div className="desktop-immediate-priorities">
            <ImmediatePrioritiesSection priorities={aiOverview.immediatePriorities || []} />
          </div>
          
          <TopContributorsSection contributors={aiOverview.bestPerformingStaff || []} />

          <PerformanceGapsSection staff={aiOverview.worstPerformingStaff || []} />

          {/* Other Insights */}
      {/*if there are no customers needing attention then put the other insights cpomponent here*/}
      {aiOverview.customersNeedingAttention?.length > 0 && (
        <OtherInsightsSection insights={aiOverview.otherInsights || []} />
      )}
          
        </div>

        {/* Column 3 */}
        <div className="overview-column">
          <NotableTrendsSection trends={aiOverview.notableTrends || []} />
          {/* Velocity Chart */}
          {dashboardMetrics?.velocityMonthlyData && Object.keys(dashboardMetrics.velocityMonthlyData).length > 0 && (
            <div>
              <div className="overview-chart-padding" style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #3182ce',
                height: '100%',
                minHeight: '300px',
                position: 'relative'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <h2 style={{
                    color: '#3182ce',
                    margin: '0',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                  }}>
                    <TrendingUpIcon style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Team Iteration Completion Trend
                  </h2>
                  {/* Info Icon */}
                  <div
                    data-velocity-info-icon
                    style={{
                      cursor: 'pointer',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowVelocityTooltip(!showVelocityTooltip);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#007bff';
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                      const svg = e.currentTarget.querySelector('svg') as SVGElement;
                      if (svg) {
                        svg.style.stroke = 'white';
                        const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                        if (circle) circle.style.fill = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      const svg = e.currentTarget.querySelector('svg') as SVGElement;
                      if (svg) {
                        svg.style.stroke = '#666';
                        const circle = svg.querySelector('circle[fill]') as SVGCircleElement;
                        if (circle) circle.style.fill = '#666';
                      }
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#666"
                      strokeWidth="2"
                      style={{ transition: 'stroke 0.2s ease' }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="8" r="1" fill="#666" stroke="none" />
                      <line x1="12" y1="11" x2="12" y2="16" />
                    </svg>
                  </div>
                </div>
                {/* Tooltip */}
                {showVelocityTooltip && (
                  <div
                    data-velocity-tooltip
                    style={{
                      position: 'absolute',
                      top: '55px',
                      right: '10px',
                      backgroundColor: '#333',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      maxWidth: '280px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 1000,
                      border: '1px solid #555'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '15px' }}>
                    Team Iteration Completion Trend
                    </div>
                    <div>
                    The team iteration completion percentage indicates how much of the planned work was actually delivered. This helps identify performance trends and compare project delivery consistency over time.
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '15px',
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#333',
                        transform: 'rotate(45deg)',
                        border: '1px solid #555',
                        borderBottom: 'none',
                        borderRight: 'none'
                      }}
                    />
                  </div>
                )}
                <div style={{ height: '200px', position: 'relative' }}>
                  <Line data={velocityChartData} options={velocityChartOptions} />
                </div>
                <p style={{
                  margin: '1rem 0 0',
                  fontSize: '0.875rem',
                  color: '#666',
                  textAlign: 'center'
                }}>
                  Iteration completion percentage over time
                </p>
              </div>
            </div>
          )}
          <PainPointsSection painPoints={aiOverview.painPoints} />
        </div>
      </div>

      
    </div>
  );
};

export default Overview;
